/**
 * GestionRecibos.jsx - Módulo de Gestión Administrativa de Recibos
 * 
 * Funcionalidades:
 * - Visualización de resumen anual de recibos por mes
 * - Métricas de firmados vs pendientes
 * - Detalle mensual con lista completa de recibos
 * - Búsqueda por legajo
 * - Descarga de PDFs individuales
 * 
 * Acceso: AdminLevel 2+ (Avanzado y Super Admin)
 */

import React, { useState, useEffect, useMemo } from 'react';
import { 
  Container, Row, Col, Card, Form, Table, Badge, 
  ProgressBar, Button, Alert, Spinner, Modal, InputGroup 
} from 'react-bootstrap';
import { 
  FaFileInvoice, FaCheckCircle, FaClock, FaSearch, 
  FaDownload, FaChevronLeft, FaCalendarAlt, FaUserAlt,
  FaFilePdf, FaExclamationTriangle 
} from 'react-icons/fa';
import axios from 'axios';
import NavbarG from '../components/NavbarG';
import '../styles/GestionRecibos.css';

const API_URL = process.env.NODE_ENV === 'development' 
  ? 'http://localhost:4005' 
  : '';

const MESES_NOMBRES = {
  '01': 'Enero',
  '02': 'Febrero',
  '03': 'Marzo',
  '04': 'Abril',
  '05': 'Mayo',
  '06': 'Junio',
  '07': 'Julio',
  '08': 'Agosto',
  '09': 'Septiembre',
  '10': 'Octubre',
  '11': 'Noviembre',
  '12': 'Diciembre'
};

const GestionRecibos = () => {
  // Estado principal
  const [anosDisponibles, setAnosDisponibles] = useState([]);
  const [anoSeleccionado, setAnoSeleccionado] = useState(new Date().getFullYear());
  const [resumenAnual, setResumenAnual] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Estado para detalle mensual
  const [mostrarDetalle, setMostrarDetalle] = useState(false);
  const [mesDetalle, setMesDetalle] = useState(null);
  const [recibosDetalle, setRecibosDetalle] = useState([]);
  const [loadingDetalle, setLoadingDetalle] = useState(false);

  // Estado para búsqueda
  const [filtroLegajo, setFiltroLegajo] = useState('');

  // Cargar años disponibles al montar
  useEffect(() => {
    cargarAnosDisponibles();
  }, []);

  // Cargar resumen cuando cambia el año
  useEffect(() => {
    if (anoSeleccionado) {
      cargarResumenAnual();
    }
  }, [anoSeleccionado]);

  // Filtrar recibos usando useMemo para mejor performance
  const recibosDetalleFiltrados = useMemo(() => {
    if (filtroLegajo === '') {
      return recibosDetalle;
    }
    const filtroLower = filtroLegajo.toLowerCase();
    return recibosDetalle.filter(r => 
      String(r.Legajo).includes(filtroLegajo) || 
      (r.NmEmpleado && r.NmEmpleado.toLowerCase().includes(filtroLower))
    );
  }, [filtroLegajo, recibosDetalle]);

  /**
   * Cargar lista de años con recibos
   */
  const cargarAnosDisponibles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/recibos/admin/anos-disponibles`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setAnosDisponibles(response.data.years);
        
        // Si el año actual no está en la lista, seleccionar el más reciente
        const añoActual = new Date().getFullYear();
        if (!response.data.years.includes(añoActual) && response.data.years.length > 0) {
          setAnoSeleccionado(response.data.years[0]);
        }
      }
    } catch (err) {
      console.error('Error al cargar años:', err);
      // No mostrar error crítico, usar año actual por defecto
    }
  };

  /**
   * Cargar resumen anual de recibos
   */
  const cargarResumenAnual = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/recibos/admin/resumen-anual/${anoSeleccionado}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setResumenAnual(response.data.meses);
      }
    } catch (err) {
      console.error('Error al cargar resumen:', err);
      setError(err.response?.data?.error || 'Error al cargar resumen de recibos');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Cargar detalle de un mes específico
   */
  const cargarDetalleMes = async (mes) => {
    // Extraer mes y año del formato MM/YYYY
    const [mesNum, year] = mes.split('/');
    
    setLoadingDetalle(true);
    setMesDetalle(mes);
    setMostrarDetalle(true);
    setFiltroLegajo(''); // Limpiar filtro

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/recibos/admin/detalle-mes/${year}/${mesNum}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setRecibosDetalle(response.data.recibos);
      }
    } catch (err) {
      console.error('Error al cargar detalle:', err);
      setError(err.response?.data?.error || 'Error al cargar detalle del mes');
    } finally {
      setLoadingDetalle(false);
    }
  };

  /**
   * Descargar PDF de un recibo
   */
  const descargarRecibo = (recibo) => {
    const { Mes, Secuencia, Legajo } = recibo;
    const [mesNum, year] = Mes.split('/');
    
    const token = localStorage.getItem('token');
    const url = `${API_URL}/api/recibos/${mesNum}/${year}/${Secuencia}/${Legajo}/pdf`;
    
    // Abrir en nueva pestaña con token
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    
    // Agregar token como header (navegador lo manejará)
    fetch(url, {
      headers: { Authorization: `Bearer ${token}` }
    })
    .then(response => response.blob())
    .then(blob => {
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `Recibo_${Legajo}_${Mes.replace('/', '-')}_Seq${Secuencia}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
    })
    .catch(err => {
      console.error('Error al descargar:', err);
      alert('Error al descargar el recibo. Verifique que el archivo existe.');
    });
  };

  /**
   * Cerrar modal de detalle
   */
  const cerrarDetalle = () => {
    setMostrarDetalle(false);
    setMesDetalle(null);
    setRecibosDetalle([]);
    setFiltroLegajo('');
  };

  /**
   * Calcular métricas generales del año usando useMemo para optimizar
   */
  const metricas = useMemo(() => {
    if (resumenAnual.length === 0) {
      return { totalRecibos: 0, totalFirmados: 0, totalPendientes: 0, promedioFirmado: 0 };
    }

    // Convertir explícitamente a Number para evitar concatenación de strings
    const totalRecibos = resumenAnual.reduce((sum, m) => sum + Number(m.total), 0);
    const totalFirmados = resumenAnual.reduce((sum, m) => sum + Number(m.firmados), 0);
    const totalPendientes = resumenAnual.reduce((sum, m) => sum + Number(m.pendientes), 0);
    const promedioFirmado = totalRecibos > 0 ? ((totalFirmados / totalRecibos) * 100).toFixed(2) : 0;

    return { totalRecibos, totalFirmados, totalPendientes, promedioFirmado };
  }, [resumenAnual]);

  return (
    <>
      {/* <NavbarG /> */}
      <Container fluid className="gestion-recibos-container py-4">
        {/* Header */}
        <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="mb-1">
                <FaFileInvoice className="me-2 text-primary" />
                Gestión de Recibos
              </h2>
              <p className="text-muted mb-0">
                Visualización y control de recibos cargados en el sistema
              </p>
            </div>
            
            {/* Selector de año */}
            <div className="d-flex align-items-center gap-2">
              <FaCalendarAlt className="text-muted" />
              <Form.Select 
                value={anoSeleccionado}
                onChange={(e) => setAnoSeleccionado(parseInt(e.target.value))}
                style={{ width: '150px' }}
                disabled={loading}
              >
                {anosDisponibles.length > 0 ? (
                  anosDisponibles.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))
                ) : (
                  <>
                    <option value={2024}>2024</option>
                    <option value={2025}>2025</option>
                    <option value={2026}>2026</option>
                  </>
                )}
              </Form.Select>
            </div>
          </div>
        </Col>
      </Row>

      {/* Mensajes de error */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          <FaExclamationTriangle className="me-2" />
          {error}
        </Alert>
      )}

      {/* Métricas generales */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="metric-card border-0 shadow-sm h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <p className="text-muted mb-1 small">Total Recibos</p>
                  <h3 className="mb-0">{Number(metricas.totalRecibos).toLocaleString('es-AR')}</h3>
                </div>
                <div className="metric-icon bg-primary">
                  <FaFileInvoice />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3}>
          <Card className="metric-card border-0 shadow-sm h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <p className="text-muted mb-1 small">Firmados</p>
                  <h3 className="mb-0 text-success">{Number(metricas.totalFirmados).toLocaleString('es-AR')}</h3>
                </div>
                <div className="metric-icon bg-success">
                  <FaCheckCircle />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3}>
          <Card className="metric-card border-0 shadow-sm h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <p className="text-muted mb-1 small">Pendientes</p>
                  <h3 className="mb-0 text-warning">{Number(metricas.totalPendientes).toLocaleString('es-AR')}</h3>
                </div>
                <div className="metric-icon bg-warning">
                  <FaClock />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3}>
          <Card className="metric-card border-0 shadow-sm h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <p className="text-muted mb-1 small">% Firmado</p>
                  <h3 className="mb-0 text-info">{Number(metricas.promedioFirmado).toLocaleString('es-AR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}%</h3>
                </div>
                <div className="metric-icon bg-info">
                  <FaCheckCircle />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Tabla resumen mensual */}
      <Card className="shadow-sm">
        <Card.Header className="bg-white">
          <h5 className="mb-0">Resumen Mensual - {anoSeleccionado}</h5>
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2 text-muted">Cargando resumen...</p>
            </div>
          ) : resumenAnual.length === 0 ? (
            <div className="text-center py-5">
              <FaExclamationTriangle size={48} className="text-muted mb-3" />
              <p className="text-muted">No hay recibos cargados para el año {anoSeleccionado}</p>
            </div>
          ) : (
            <Table responsive hover className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>Mes</th>
                  <th className="text-center">Total</th>
                  <th className="text-center">Firmados</th>
                  <th className="text-center">Pendientes</th>
                  <th style={{ width: '300px' }}>Progreso</th>
                  <th className="text-center">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {resumenAnual.map((mes, index) => {
                  const [mesNum] = mes.Mes.split('/');
                  const nombreMes = MESES_NOMBRES[mesNum] || mesNum;

                  return (
                    <tr key={index}>
                      <td>
                        <strong>{nombreMes} {anoSeleccionado}</strong>
                        <br />
                        <small className="text-muted">{mes.Mes}</small>
                      </td>
                      <td className="text-center">
                        <Badge bg="secondary" pill>{Number(mes.total).toLocaleString('es-AR')}</Badge>
                      </td>
                      <td className="text-center">
                        <Badge bg="success" pill>
                          <FaCheckCircle className="me-1" />
                          {Number(mes.firmados).toLocaleString('es-AR')}
                        </Badge>
                      </td>
                      <td className="text-center">
                        <Badge bg="warning" pill>
                          <FaClock className="me-1" />
                          {Number(mes.pendientes).toLocaleString('es-AR')}
                        </Badge>
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          <ProgressBar 
                            now={mes.porcentaje_firmados} 
                            label={`${mes.porcentaje_firmados}%`}
                            variant={
                              mes.porcentaje_firmados >= 80 ? 'success' :
                              mes.porcentaje_firmados >= 50 ? 'warning' : 'danger'
                            }
                            className="flex-grow-1"
                            style={{ height: '25px' }}
                          />
                        </div>
                      </td>
                      <td className="text-center">
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => cargarDetalleMes(mes.Mes)}
                        >
                          <FaSearch className="me-1" />
                          Ver Detalle
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Modal de detalle mensual */}
      <Modal 
        show={mostrarDetalle} 
        onHide={cerrarDetalle}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FaFilePdf className="me-2 text-danger" />
            Detalle de Recibos - {mesDetalle && MESES_NOMBRES[mesDetalle.split('/')[0]]} {mesDetalle && mesDetalle.split('/')[1]}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Buscador */}
          <Row className="mb-3">
            <Col md={6}>
              <InputGroup>
                <InputGroup.Text>
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Buscar por legajo o nombre..."
                  value={filtroLegajo}
                  onChange={(e) => setFiltroLegajo(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col md={6} className="text-end">
              <small className="text-muted">
                Mostrando {recibosDetalleFiltrados.length} de {recibosDetalle.length} recibos
              </small>
            </Col>
          </Row>

          {/* Tabla de detalle */}
          {loadingDetalle ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2">Cargando recibos...</p>
            </div>
          ) : recibosDetalleFiltrados.length === 0 ? (
            <Alert variant="info">
              <FaExclamationTriangle className="me-2" />
              {filtroLegajo ? 'No se encontraron recibos con ese filtro' : 'No hay recibos para este mes'}
            </Alert>
          ) : (
            <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
              <Table striped hover responsive className="mb-0">
                <thead className="table-light sticky-top">
                  <tr>
                    <th>Legajo</th>
                    <th>Empleado</th>
                    <th className="text-center">Sec.</th>
                    <th className="text-center">Estado</th>
                    <th>Fecha/Hora</th>
                    <th className="text-center">Valida</th>
                    <th className="text-center">Protesta</th>
                    <th className="text-center">Resuelto</th>
                    <th className="text-center">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {recibosDetalleFiltrados.map((recibo, index) => (
                    <tr key={index}>
                      <td>
                        <FaUserAlt className="me-1 text-muted" />
                        <strong>{recibo.Legajo}</strong>
                      </td>
                      <td>{recibo.NmEmpleado || <em className="text-muted">Sin nombre</em>}</td>
                      <td className="text-center">
                        <Badge bg="secondary">{recibo.Secuencia}</Badge>
                      </td>
                      <td className="text-center">
                        {recibo.Firmado === 1 ? (
                          <Badge bg="success">
                            <FaCheckCircle className="me-1" />
                            Firmado
                          </Badge>
                        ) : (
                          <Badge bg="warning" text="dark">
                            <FaClock className="me-1" />
                            Pendiente
                          </Badge>
                        )}
                      </td>
                      <td>
                        <small>
                          {recibo.FechayHora 
                            ? new Date(recibo.FechayHora).toLocaleString('es-AR')
                            : '-'}
                        </small>
                      </td>
                      <td className="text-center">
                        {recibo.Valida === 1 ? (
                          <Badge bg="success" pill>Sí</Badge>
                        ) : (
                          <Badge bg="secondary" pill>No</Badge>
                        )}
                      </td>
                      <td className="text-center">
                        {recibo.Protesta === 1 ? (
                          <Badge bg="danger" pill>Sí</Badge>
                        ) : (
                          <Badge bg="secondary" pill>No</Badge>
                        )}
                      </td>
                      <td className="text-center">
                        {recibo.Resuelto === 1 ? (
                          <Badge bg="success" pill>Sí</Badge>
                        ) : (
                          <Badge bg="secondary" pill>No</Badge>
                        )}
                      </td>
                      <td className="text-center">
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => descargarRecibo(recibo)}
                          title="Descargar PDF"
                        >
                          <FaDownload />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={cerrarDetalle}>
            <FaChevronLeft className="me-1" />
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
    </>
  );
};

export default GestionRecibos;
